import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          clearCart();
          const orderRes = await axios.get(`${API}/orders/by-session/${sessionId}`);
          setOrder(orderRes.data);
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setStatus("timeout");
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FDFBF7]" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-lg w-full border border-[#E5DFD3] bg-[#FDFBF7] p-10 lg:p-14"
      >
        {status === "checking" && (
          <div className="flex flex-col items-start gap-6" data-testid="payment-checking">
            <Loader2 size={32} className="animate-spin text-[#4A148C]" />
            <h1 className="font-serif text-4xl font-light tracking-tight">Confirming your order…</h1>
            <p className="text-sm font-light text-[#1A1A1A]/60">One tick while we speak to the bank.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-confirmed">
            <div className="w-14 h-14 bg-[#4A148C] text-[#FDFBF7] flex items-center justify-center mb-8">
              <Check size={26} />
            </div>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#4A148C] mb-3">Order confirmed</p>
            <h1 className="font-serif text-5xl font-light tracking-tighter leading-none">
              Proppa <span className="italic">choice.</span>
            </h1>
            {order && (
              <div className="mt-8 text-sm font-light text-[#1A1A1A]/70 space-y-2" data-testid="order-summary">
                <p className="font-medium text-[#1A1A1A]">Order {order.order_id}</p>
                {order.items.map((i) => (
                  <p key={i.lookup_key} className="flex justify-between">
                    <span>{i.name} × {i.quantity}</span>
                    <span>{i.free ? "FREE" : `£${((i.unit_amount * i.quantity) / 100).toFixed(2)}`}</span>
                  </p>
                ))}
                <p className="pt-2 border-t border-[#E5DFD3] flex justify-between font-medium text-[#1A1A1A]">
                  <span>Total</span>
                  <span>£{(order.amount / 100).toFixed(2)}</span>
                </p>
              </div>
            )}
            <p className="mt-6 text-sm font-light text-[#1A1A1A]/60">
              A confirmation email is on its way. Your snacks will be with you in 2–3 working days.
            </p>
            {order?.fulfillment === "dispatched" && (
              <div className="mt-6 border border-[#FF6B00] bg-[#FF6B00]/10 px-5 py-4" data-testid="order-tracking">
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#1A1A1A]">Dispatched</p>
                {order.tracking_url ? (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="order-tracking-link"
                    className="mt-2 inline-block text-sm font-medium text-[#4A148C] hover:underline"
                  >
                    Track your parcel ({order.tracking_code})
                  </a>
                ) : (
                  <p className="mt-2 text-sm font-light text-[#1A1A1A]/60">Your parcel is on its way.</p>
                )}
              </div>
            )}
          </div>
        )}
        {(status === "timeout" || status === "error") && (
          <div data-testid="payment-unconfirmed">
            <h1 className="font-serif text-4xl font-light tracking-tight">Still confirming…</h1>
            <p className="mt-4 text-sm font-light text-[#1A1A1A]/60">
              Your payment is being processed. If you were charged, your order confirmation will arrive by email shortly.
            </p>
          </div>
        )}
        <Link
          to="/"
          data-testid="back-to-shop-link"
          className="mt-10 inline-block border border-[#1A1A1A] px-8 py-4 text-xs font-semibold tracking-[0.2em] uppercase hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors duration-300"
        >
          Back to the shop
        </Link>
      </motion.div>
    </div>
  );
}
